import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';
import '../styles/Terms.css';

export default function ShippingTerms() {
  const heroRef = useRef(null);
  const wrapRef = useRef(null);
  const tocRef  = useRef(null);

  // Sticky TOC (JS-managed fallback for cases where native sticky is broken by transforms/overflow)
  useEffect(() => {
    const header = document.querySelector('.header'); // adjust if your fixed header uses a different selector
    const getHeaderH = () => (header?.offsetHeight || 94);

    const syncFixedMetrics = () => {
      if (!tocRef.current || !wrapRef.current) return;
      const wrapRect = wrapRef.current.getBoundingClientRect();
      tocRef.current.style.setProperty('--toc-left', `${wrapRect.left + window.scrollX}px`);
      tocRef.current.style.setProperty('--toc-width', '280px');
      tocRef.current.style.setProperty('--sticky-top', `${getHeaderH() + 16}px`);
    };

    const onScroll = () => {
      if (!heroRef.current || !tocRef.current || !wrapRef.current) return;

      // bottom cap so TOC stops before the end of page content
      const stickyTop = getHeaderH() + 16;
      const wrapRect = wrapRef.current.getBoundingClientRect();
      const tocH = tocRef.current.offsetHeight;
      const currentBottom = window.scrollY + stickyTop + tocH;
      const boundaryBottom = (document.getElementById('support')?.getBoundingClientRect().bottom ?? wrapRect.bottom) + window.scrollY;
      if (currentBottom > boundaryBottom) {
        tocRef.current.classList.add('is-capped');
        tocRef.current.style.setProperty('--cap-y', `${boundaryBottom - currentBottom}px`);
        syncFixedMetrics();
      } else {
        tocRef.current.classList.remove('is-capped');
        tocRef.current.style.removeProperty('--cap-y');
      }

      // only manage on tablet/desktop; phones fall back to flow
      if (window.innerWidth < 769) {
        tocRef.current.classList.remove('is-fixed');
        tocRef.current.style.removeProperty('--toc-left');
        tocRef.current.style.removeProperty('--toc-width');
        return;
      }

      const heroBottom = heroRef.current.getBoundingClientRect().bottom;
      if (heroBottom <= getHeaderH() + 16) {
        tocRef.current.classList.add('is-fixed');
        syncFixedMetrics();
      } else {
        tocRef.current.classList.remove('is-fixed');
      }
    };

    const onResize = () => { onScroll(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    onScroll(); // initial pass

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  const lastUpdated = 'August 12, 2025';

  return (
    <main className="lenis-content terms-page" aria-labelledby="terms-title">
      {/* Hero */}
      <section ref={heroRef} className="terms-hero">
        <div className="terms-hero__content">
          <h1 id="terms-title" className="terms-hero__title">Shipping Terms & Conditions</h1>
          <p className="terms-hero__subtitle">
            General shipping, delivery, and claims terms for industrial equipment. Designed to be clear and
            practical for freight, LTL, flatbed, and rigging scenarios.
          </p>
        </div>
        <div className="terms-hero__overlay" aria-hidden="true" />
      </section>

      {/* Body */}
      <section ref={wrapRef} className="terms-wrap">
        {/* On‑this‑page / ToC */}
        <aside ref={tocRef} className="terms-toc" aria-label="On this page">
          <div className="toc-card">
            <h2 className="toc-title">On this page</h2>
            <nav className="toc-nav">
              <a href="#scope">1. Scope & Applicability</a>
              <a href="#incoterms">2. Delivery Terms & Incoterms®</a>
              <a href="#scheduling">3. Scheduling & Delivery Windows</a>
              <a href="#packaging">4. Packaging, Crating & Handling</a>
              <a href="#carrier">5. Carrier Selection & Routing Control</a>
              <a href="#freight">6. Freight Terms & Charges</a>
              <a href="#accessorials">7. Accessorials & Site Conditions</a>
              <a href="#risk">8. Title & Risk of Loss</a>
              <a href="#inspection">9. Inspection at Delivery & Acceptance</a>
              <a href="#claims">10. Damage/Loss Claims (Visible & Concealed)</a>
              <a href="#redelivery">11. Refused Delivery, Redelivery & Storage</a>
              <a href="#export">12. Export/Import, Customs & Compliance</a>
              <a href="#hazmat">13. Oils, PCBs, Hazardous Materials</a>
              <a href="#force">14. Force Majeure & Carrier Delays</a>
              <a href="#address">15. Address Accuracy & Change Fees</a>
              <a href="#changes">16. Changes, Cancellations & Reroutes</a>
              <a href="#support">17. Shipping Support & Contact</a>
            </nav>
          </div>
        </aside>

        {/* Terms content */}
        <article className="terms-body">
          <p className="terms-updated">
            Last updated: {lastUpdated}. <span className="legal-note">These Shipping Terms are general
            information and not legal advice. Please have your counsel review for your operations and
            jurisdictions.</span>
          </p>

          <section id="scope" className="terms-section">
            <h2>1. Scope & Applicability</h2>
            <p>
              These Shipping Terms & Conditions ("Shipping Terms") apply to freight, parcel, and specialized
              transport for industrial equipment, parts, and related services handled by our company ("Company").
              They govern shipments we arrange on your behalf (prepaid), shipments you arrange (collect/third‑party),
              and customer pickups. If a signed agreement or quote/order acknowledgement specifies different
              shipping terms, that document controls for the conflicting provision.
            </p>
          </section>

          <section id="incoterms" className="terms-section">
            <h2>2. Delivery Terms & Incoterms®</h2>
            <ul>
              <li><strong>Domestic (U.S.).</strong> Unless stated otherwise, shipments follow F.O.B. shipping point. Risk of loss transfers to you upon carrier tender; title per Section 8.</li>
              <li><strong>International.</strong> We may reference Incoterms® 2020 (e.g., EXW, FCA, CPT, DAP). The chosen rule on the quote controls responsibilities for risk, carriage, export/import clearance, and costs.</li>
              <li><strong>Written specification.</strong> The delivery term appearing on our quote/order acknowledgement/invoice overrides any contrary boilerplate on your PO.</li>
            </ul>
          </section>

          <section id="scheduling" className="terms-section">
            <h2>3. Scheduling & Delivery Windows</h2>
            <ul>
              <li><strong>Target dates.</strong> Dates are good‑faith estimates based on inventory readiness, carrier availability, and site readiness.</li>
              <li><strong>Appointments.</strong> Carriers may require appointment windows; missed appointments can incur redelivery or wait‑time fees.</li>
              <li><strong>Customer pickup.</strong> Bring proper equipment and tie‑downs; follow site safety rules. We may refuse loading if unsafe.</li>
            </ul>
          </section>

          <section id="packaging" className="terms-section">
            <h2>4. Packaging, Crating & Handling</h2>
            <ul>
              <li><strong>Standard.</strong> Palletized, banded, or crated as appropriate for LTL/flatbed. Heavy/oversize items may ship skidded or loose with rigging points.</li>
              <li><strong>Special requests.</strong> Export crating, vapor barrier, shock/tilt indicators, or weatherproofing are available at additional cost.</li>
              <li><strong>Inspection before load.</strong> We may photograph packaging; please do the same at receipt.</li>
            </ul>
          </section>

          <section id="carrier" className="terms-section">
            <h2>5. Carrier Selection & Routing Control</h2>
            <ul>
              <li><strong>Company‑arranged freight (Prepaid/Add).</strong> We select reputable carriers and routing at our discretion unless a carrier is specified in writing.</li>
              <li><strong>Customer‑arranged freight (Collect/3rd‑party).</strong> You control carrier choice and are responsible for transit, schedules, and claims with your carrier.</li>
              <li><strong>Right to substitute.</strong> We may substitute equivalent carriers or modes to meet schedule/weight constraints.</li>
            </ul>
          </section>

          <section id="freight" className="terms-section">
            <h2>6. Freight Terms & Charges</h2>
            <ul>
              <li><strong>Prepay & Add.</strong> Freight we arrange appears as a separate line and is invoiced to you.</li>
              <li><strong>Collect/3rd‑party.</strong> Freight bills go directly to your carrier account; you are liable for all charges and adjustments.</li>
              <li><strong>Detention/Wait time.</strong> Assessed at carrier rates when loading/unloading exceeds standard free time.</li>
              <li><strong>Reweigh/reclass.</strong> Carriers may audit and reclass; you are responsible for up‑ or down‑charges issued by the carrier.</li>
            </ul>
          </section>

          <section id="accessorials" className="terms-section">
            <h2>7. Accessorials & Site Conditions</h2>
            <ul>
              <li><strong>Common accessorials.</strong> Liftgate, inside delivery, residential/limited access, job‑site, construction site, appointment, after‑hours, and oversized handling.</li>
              <li><strong>Rigging & offloading.</strong> Unless our quote includes rigging, you provide forklifts/cranes, qualified riggers, and safe access. Drivers are not riggers.</li>
              <li><strong>Refusals due to site readiness.</strong> If the site cannot safely receive the load, redelivery/layover fees apply.</li>
            </ul>
          </section>

          <section id="risk" className="terms-section">
            <h2>8. Title & Risk of Loss</h2>
            <p>
              Risk of loss transfers per the stated delivery term (F.O.B. shipping point or the named Incoterm®). Title typically transfers upon full payment unless otherwise agreed. See your quote/order acknowledgement for any variation.
            </p>
          </section>

          <section id="inspection" className="terms-section">
            <h2>9. Inspection at Delivery & Acceptance</h2>
            <ul>
              <li><strong>Before signing.</strong> Inspect packaging and goods; note any visible damage/shortage on the delivery receipt (Photos strongly recommended).</li>
              <li><strong>Concealed issues.</strong> Unpack promptly; see Claims (Section 10) for timelines.</li>
              <li><strong>Use/installation.</strong> Use or installation may constitute acceptance of delivered goods.</li>
            </ul>
          </section>

          <section id="claims" className="terms-section">
            <h2>10. Damage/Loss Claims (Visible & Concealed)</h2>
            <ul>
              <li><strong>Carrier responsibility.</strong> Damage/loss in transit is generally a carrier matter. Preserve packaging and provide photos, delivery receipt notes, and serials.</li>
              <li><strong>Visible damage/shortage.</strong> Note specifics on the bill of lading at delivery and notify us within 24 hours.</li>
              <li><strong>Concealed damage.</strong> Notify us in writing as soon as discovered—no later than 5 business days from delivery—so we can initiate a claim window with the carrier.</li>
              <li><strong>Mitigation.</strong> Do not use damaged equipment until inspection guidance is provided.</li>
            </ul>
          </section>

          <section id="redelivery" className="terms-section">
            <h2>11. Refused Delivery, Redelivery & Storage</h2>
            <ul>
              <li><strong>Refusals.</strong> If refused for reasons other than carrier damage/shortage properly noted, you are responsible for return freight, storage, and redelivery fees.</li>
              <li><strong>Inability to deliver.</strong> If the carrier cannot deliver due to site issues or unavailable personnel/equipment, redelivery or layover charges apply.</li>
              <li><strong>Storage/Abandonment.</strong> Freight placed into storage incurs storage fees at published rates; after a reasonable period, items may be deemed abandoned as permitted by law.</li>
            </ul>
          </section>

          <section id="export" className="terms-section">
            <h2>12. Export/Import, Customs & Compliance</h2>
            <ul>
              <li><strong>Licenses & filings.</strong> You are responsible for any export/import licenses, AES filings, and customs brokerage unless we explicitly agree otherwise.</li>
              <li><strong>Sanctions & controls.</strong> You represent that destinations, end‑users, and end‑uses comply with applicable export control and sanctions laws.</li>
            </ul>
          </section>

          <section id="hazmat" className="terms-section">
            <h2>13. Oils, PCBs, Hazardous Materials</h2>
            <ul>
              <li><strong>Declarations.</strong> If units contain oil or hazardous substances, you must disclose prior to shipment. Special handling/labeling may be required.</li>
              <li><strong>Environmental compliance.</strong> Parties will comply with DOT/IMDG/IATA and applicable environmental regulations. Additional costs for hazmat packaging/permits are your responsibility unless stated otherwise.</li>
            </ul>
          </section>

          <section id="force" className="terms-section">
            <h2>14. Force Majeure & Carrier Delays</h2>
            <p>
              Neither party is liable for delays caused by events beyond reasonable control, including weather, strikes, embargoes, material shortages, government actions, or carrier disruptions. The affected party will notify the other and resume performance as practicable.
            </p>
          </section>

          <section id="address" className="terms-section">
            <h2>15. Address Accuracy & Change Fees</h2>
            <ul>
              <li><strong>Accuracy.</strong> You are responsible for providing complete ship‑to details, hours, contact names, and delivery constraints.</li>
              <li><strong>Corrections.</strong> Reconsignment, reroute, and address corrections are billed at carrier rates plus admin fees if applicable.</li>
            </ul>
          </section>

          <section id="changes" className="terms-section">
            <h2>16. Changes, Cancellations & Reroutes</h2>
            <p>
              Changes after dispatch (mode, address, service level) are subject to carrier approval and may incur additional charges and delays. Cancellations may not be possible once freight is tendered. Any approved reroute/reconsignment will be billed at carrier rates.
            </p>
          </section>

          <section id="support" className="terms-section">
            <h2>17. Shipping Support & Contact</h2>
            <p>
              For routing questions, delivery coordination, or claims assistance, please <Link to="/contact-us">contact us</Link>. For bank/wire verification (fraud prevention), call the phone number listed on our website.
            </p>
            <div className="terms-callout" role="note">
              <p>
                <strong>Tip:</strong> Pair these Shipping Terms with your Payment Terms. Together they clarify when risk transfers, how freight is billed, and how to handle claims.
              </p>
            </div>
          </section>
        </article>
      </section>

      <Footer />
    </main>
  );
}
